import axios from 'axios';
import { apiUrl } from './config';
import { getAuthToken } from '@/utils/auth';

/**
 * Get all chapter plans for the teacher with optional filters
 * @param {Object} filters - Optional filters (grade_id, subject_id, chapter_id)
 * @returns {Promise} API response
 */
export const getPlans = async (filters = {}) => {
  const token = getAuthToken('teacher');
  if (!token) {
    console.error('❌ No authentication token found');
    throw new Error('No authentication token found');
  }

  const params = new URLSearchParams();
  if (filters.grade_id) params.append('grade_id', filters.grade_id);
  if (filters.subject_id) params.append('subject_id', filters.subject_id);
  if (filters.chapter_id) params.append('chapter_id', filters.chapter_id);

  const queryString = params.toString();
  const baseUrl = apiUrl('v1/teachers/ai-tools/chapter-planner/');
  const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;
  
  console.log('📡 API Request Details:');
  console.log('  URL:', url);
  console.log('  Method: GET');
  console.log('  Has token:', !!token);
  console.log('  Token preview:', token.substring(0, 20) + '...');
  console.log('  Filters:', filters);
  
  try {
  const response = await axios.get(url, {
    headers: { Authorization: `Bearer ${token}` }
  });
  
    console.log('✅ API Response:');
    console.log('  Status:', response.status);
    console.log('  Status text:', response.statusText);
    console.log('  Data type:', typeof response.data);
    console.log('  Data is array:', Array.isArray(response.data));
    console.log('  Data length:', Array.isArray(response.data) ? response.data.length : 'N/A');
    console.log('  Full data:', response.data);
    
    // Backend returns array directly when serializer has many=True
  return response.data;
  } catch (error) {
    console.error('❌ API Error:');
    console.error('  Error message:', error.message);
    console.error('  Response status:', error.response?.status);
    console.error('  Response data:', error.response?.data);
    console.error('  Response headers:', error.response?.headers);
    throw error;
  }
};

/**
 * Get a specific chapter plan with all periods
 * @param {number} planId - Plan ID
 * @returns {Promise} API response
 */
export const getPlan = async (planId) => {
  const token = getAuthToken('teacher');
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await axios.get(
    apiUrl(`v1/teachers/ai-tools/chapter-planner/${planId}/`),
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  
  return response.data;
};

/**
 * Create a new chapter plan (triggers generation)
 * @param {Object} data - Plan data (grade_id, subject_id, chapter_id, total_periods, period_duration)
 * @returns {Promise} API response
 */
export const createPlan = async (data) => {
  const token = getAuthToken('teacher');
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await axios.post(
    apiUrl('v1/teachers/ai-tools/chapter-planner/create/'),
    data,
    {
      headers: { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );
  
  return response.data;
};

/**
 * Update a chapter plan
 * @param {number} planId - Plan ID
 * @param {Object} data - Updated plan data (total_periods, period_duration)
 * @returns {Promise} API response
 */
export const updatePlan = async (planId, data) => {
  const token = getAuthToken('teacher');
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await axios.put(
    apiUrl(`v1/teachers/ai-tools/chapter-planner/${planId}/`),
    data,
    {
      headers: { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );
  
  return response.data;
};

/**
 * Delete a chapter plan (soft delete - sets is_active to false)
 * @param {number} planId - Plan ID
 * @returns {Promise} API response
 */
export const deletePlan = async (planId) => {
  const token = getAuthToken('teacher');
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await axios.delete(
    apiUrl(`v1/teachers/ai-tools/chapter-planner/${planId}/`),
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  
  return response.data;
};

/**
 * Reset a plan (regenerate from scratch)
 * @param {number} planId - Plan ID
 * @returns {Promise} API response
 */
export const resetPlan = async (planId) => {
  const token = getAuthToken('teacher');
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await axios.post(
    apiUrl(`v1/teachers/ai-tools/chapter-planner/${planId}/reset/`),
    {},
    {
      headers: { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );
  
  return response.data;
};

/**
 * Get all periods for a plan
 * @param {number} planId - Plan ID
 * @returns {Promise} API response
 */
export const getPeriods = async (planId) => {
  const token = getAuthToken('teacher');
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await axios.get(
    apiUrl(`v1/teachers/ai-tools/chapter-planner/${planId}/periods/`),
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  
  return response.data;
};

/**
 * Get a specific period
 * @param {number} planId - Plan ID
 * @param {number} periodNumber - Period number
 * @returns {Promise} API response
 */
export const getPeriod = async (planId, periodNumber) => {
  const token = getAuthToken('teacher');
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await axios.get(
    apiUrl(`v1/teachers/ai-tools/chapter-planner/${planId}/periods/${periodNumber}/`),
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  
  return response.data;
};

/**
 * Update a period (content edits, status, etc.)
 * @param {number} planId - Plan ID
 * @param {number} periodNumber - Period number
 * @param {Object} data - Updated period data
 * @returns {Promise} API response
 */
export const updatePeriod = async (planId, periodNumber, data) => {
  const token = getAuthToken('teacher');
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await axios.put(
    apiUrl(`v1/teachers/ai-tools/chapter-planner/${planId}/periods/${periodNumber}/`),
    data,
    {
      headers: { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );
  
  return response.data;
};

/**
 * Update period status (triggers auto-adjustment if partial/skipped)
 * @param {number} planId - Plan ID
 * @param {number} periodNumber - Period number
 * @param {string} status - New status ('pending', 'completed', 'partial', 'skipped')
 * @param {Array} completedTopics - Array of completed topic IDs (for partial status)
 * @param {number|null} divisionId - Optional division ID for division-specific status updates
 * @returns {Promise} API response with updated period and plan
 */
export const updatePeriodStatus = async (planId, periodNumber, status, completedTopics = [], divisionId = null) => {
  const token = getAuthToken('teacher');
  if (!token) {
    throw new Error('No authentication token found');
  }

  const payload = {
    status,
    completed_topics: completedTopics
  };
  
  // Add division_id if provided (for division-specific status updates)
  if (divisionId !== null && divisionId !== undefined) {
    payload.division_id = divisionId;
  }

  const response = await axios.post(
    apiUrl(`v1/teachers/ai-tools/chapter-planner/${planId}/periods/${periodNumber}/update-status/`),
    payload,
    {
      headers: { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );
  
  return response.data;
};

/**
 * Generate AI content for a period (script, notes, visuals, questions)
 * @param {number} planId - Plan ID
 * @param {number} periodNumber - Period number
 * @returns {Promise} API response with generated content
 */
export const generatePeriodContent = async (planId, periodNumber) => {
  const token = getAuthToken('teacher');
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await axios.post(
    apiUrl(`v1/teachers/ai-tools/chapter-planner/${planId}/periods/${periodNumber}/generate-content/`),
    {},
    {
      headers: { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );
  
  return response.data;
};

